import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Store } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useSettings } from '@/hooks/useProducts';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAppMounts, type AppMount } from '@/lib/apps-mounts';
import { recallVertical, verticalFromPath, verticalHref } from '@/pages/public/saas/verticals';
import { useActiveMount } from '@/hooks/useActiveMount';
import { useAppNavStore } from '@/stores/app-nav-store';
import { IS_RUMIO, IS_LODGING } from '@/config/site';
import { manifestEntryToNavLink } from './nav-types';
import type { AppNavSection, NavLink } from './nav-types';

export interface HeaderNav {
  /** Marketing nav for the main row (empty inside a replace-mode app mount). */
  navLinks: NavLink[];
  /** Installed-app pages for the account menu + mobile drawer (entitled users only). */
  visibleAppSections: AppNavSection[];
  inReplaceMode: boolean;
  showEcommerce: boolean;
  showCourses: boolean;
  /** Lucide mark used when the tenant has no logo image. */
  LogoIcon: typeof Store;
}

/**
 * The nav every header variant renders, assembled the one way the storefront
 * assembles it: the tenant's `navigation_links` (or the site-type default),
 * the rumio.world vertical rewrite, the three ways marketing links hide for
 * signed-in users, and the installed-app pages contributed by manifests and
 * runtime `rumio:nav` (contract v1.6 / v1.10).
 */
export function useHeaderNav(): HeaderNav {
  const { t } = useTranslation(['nav', 'common']);
  const { user } = useAuthStore();
  const { data: settingsData } = useSettings();
  const { isEnabled } = useFeatureFlags();
  const location = useLocation();
  const showEcommerce = isEnabled('ecommerce');
  const showCourses = isEnabled('courses');

  // Lodging tenants get a stays-shaped default nav. In practice the wizard
  // seeds `settings.navigation_links` (Stays / Local Guide / …), so this only
  // fires as a fallback when a lodging site has no seeded nav — but it keeps
  // that fallback on-brand instead of showing Blog/About/Help with no "Stays".
  const defaultNavLinks: NavLink[] = IS_LODGING
    ? [
        { label: t('nav:stays', { defaultValue: 'Stays' }), href: '/properties' },
        { label: t('nav:localGuide', { defaultValue: 'Local Guide' }), href: '/page/things-to-do' },
        { label: t('nav:houseRules', { defaultValue: 'House Rules' }), href: '/page/house-rules' },
        { label: t('nav:findReservation', { defaultValue: 'Find My Reservation' }), href: '/reservations' },
        { label: t('nav:help'), axissOpen: true },
      ]
    : [
        ...(showEcommerce ? [{ label: t('nav:shop'), href: '/shop' }] : []),
        ...(showCourses ? [{ label: t('nav:courses'), href: '/courses' }] : []),
        { label: t('nav:blog'), href: '/blog' },
        { label: t('nav:about'), href: '/page/about' },
        { label: t('nav:help'), axissOpen: true },
      ];
  const LogoIcon = showCourses && !showEcommerce ? BookOpen : Store;

  // Apps Platform v1.1+v1.6: pull nav entries declared in installed-app
  // manifests AND runtime nav contributed via `rumio:nav` (v1.6 §3).
  // Multiple mounts of the same app share one manifest; we dedupe by app id.
  // For each entry, we link to the mount whose default_path matches the
  // entry's path; otherwise fall back to the first mount with the entry
  // path appended. Children get the same prefix translation.
  const { data: mounts } = useAppMounts();
  const runtimeNavByAppId = useAppNavStore((s) => s.runtimeNavByAppId);
  const activeMountRes = useActiveMount();

  const mountsByApp = new Map<string, AppMount[]>();
  for (const m of mounts ?? []) {
    const list = mountsByApp.get(m.app.id) ?? [];
    list.push(m);
    mountsByApp.set(m.app.id, list);
  }

  function resolvedNavForApp(mount: AppMount): NavLink[] {
    const all = mountsByApp.get(mount.app.id) ?? [mount];
    const source =
      runtimeNavByAppId[mount.app.id] !== undefined
        ? runtimeNavByAppId[mount.app.id]
        : mount.app.manifest?.nav ?? null;
    if (!source || source.length === 0) return [];
    return source.map((e) => manifestEntryToNavLink(e, mount, all));
  }

  // Append-mode app nav, grouped per-app into a labeled section. Sections
  // surface inside the avatar/account dropdown (desktop) and the mobile
  // drawer's account block, gated on `user.hasActiveSubscription`. The
  // grouping per-app is intentional: a flat row of every mounted app's
  // pages bloats the host marketing nav (see plan: combined-nav).
  const appSections: AppNavSection[] = [];
  for (const appMounts of mountsByApp.values()) {
    if (!appMounts || appMounts.length === 0) continue;
    const items = resolvedNavForApp(appMounts[0]);
    if (items.length === 0) continue;
    appSections.push({
      appId: appMounts[0].app.id,
      appName: appMounts[0].app.name,
      items,
    });
  }
  // We only surface the section to entitled users — anyone else (anonymous,
  // free tier) gets no app nav at all. Tightening per-app (matching a
  // specific subscription to a specific app) is a follow-up; today's
  // tenants ship one paid app each.
  const visibleAppSections = user?.hasActiveSubscription ? appSections : [];

  const navFromSettings = settingsData?.settings?.navigation_links;
  const configuredNav: NavLink[] = Array.isArray(navFromSettings) ? navFromSettings : defaultNavLinks;
  // rumio.world has a version of Features and Pricing per site type. Once a
  // visitor is inside one (or has picked one before), those two links follow
  // it, so "Pricing" from the lodging pages is lodging pricing. The nav rows
  // themselves stay type-neutral in `navigation_links`.
  const activeVertical = IS_RUMIO
    ? verticalFromPath(location.pathname)?.id ?? recallVertical()
    : null;
  const baseNav: NavLink[] = activeVertical
    ? configuredNav.map((link) => (link.href ? { ...link, href: verticalHref(link.href, activeVertical) } : link))
    : configuredNav;
  // Three ways to hide marketing nav from authed users, from coarsest to
  // finest:
  // 1. `hide_nav_when_authed` site setting — drops EVERY marketing link
  //    from the header (and the mobile drawer's marketing section) when
  //    the user is signed in. App-shell links from installed-app manifests
  //    still render. Use this on SaaS sites whose authed users live
  //    entirely inside an embedded custom-app surface.
  // 2. `hide_when_authed` per-link flag on `navigation_links` — hides
  //    specific marketing links (Pricing, Features, etc.) but leaves
  //    others visible.
  // 3. Nothing — default: every link renders for authed users.
  const hideAllNavWhenAuthed = !!settingsData?.settings?.hide_nav_when_authed;
  const filteredBaseNav = user
    ? hideAllNavWhenAuthed
      ? []
      : baseNav.filter((link) => !link.hide_when_authed)
    : baseNav;

  // Contract v1.6 §2: when inside an app mount with effective nav_mode='replace',
  // we hide the marketing nav since the active app owns the surface.
  const inReplaceMode = !!activeMountRes && activeMountRes.effectiveNavMode === 'replace';
  // Contract v1.10 combined-nav: the active app's pages live in the
  // avatar/account dropdown (desktop) + mobile account block via
  // `visibleAppSections` — NOT in the main nav row. In replace mode the main
  // nav row is empty: the app's pages are in the dropdown, the logo already
  // links home, and the redundant "Back to {site}" affordance was dropped.
  const navLinks: NavLink[] = inReplaceMode ? [] : [...filteredBaseNav];

  return { navLinks, visibleAppSections, inReplaceMode, showEcommerce, showCourses, LogoIcon };
}
